'use client'

import styled from '@emotion/styled'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocale } from '@/hooks/useLocale'
import { colors } from '@/styles/theme'
import { ImagesSelector } from '@/components/molecules/ImagesSelector'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '../../../atoms/Inputs'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { ApexRank } from '@/firebase/firestore/ApexRank'
import { ImageSelector } from '@/components/atoms/ImageSelector'
import { TemplateSelector } from '@/components/atoms/TemplateSelector'
import { Apex1 } from './Apex1'
import { Apex2 } from './Apex2'
import { Apex3 } from './Apex3'
import { Apex4 } from './Apex4'
import { Apex5 } from './Apex5'
import { Apex7 } from './Apex7'
import { Apex8 } from './Apex8'
import { Apex9 } from './Apex9'
import { Apex10 } from './Apex10'
import { Apex11 } from './Apex11'
import { Apex12 } from './Apex12'
import { Apex13 } from './Apex13'
import { Apex14 } from './Apex14'
import { Apex15 } from './Apex15'
import { Apex16 } from './Apex16'
import { Apex17 } from './Apex17'
import { Apex18 } from './Apex18'
import { Apex19 } from './Apex19'
import { Apex20 } from './Apex20'
import { Apex21 } from './Apex21'
import { ProfileEditorAppBanner } from '../../../organisms/Banner/ProfileEditorAppBanner'
import { format } from 'date-fns'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'
import { TextSelector } from '@/components/atoms/TextSelector'
import {
  CREATOR,
  creatorMapTargetId,
  CREATORS,
  mapApexCreatorInfo,
} from './config'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'

/**
 * プラットフォーム事にフォームが違う
 *
 */

type FormField = {
  id: string
  twitterId: string
  name: string
  playStyle: string
  inviteStyle: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playTime: string
  apexStartingSeason: string
  apexFavoriteWeapon: string
  apexMaxDamage: number
  apexKD: number
  memo: string
  hitokoto: string
  apexRank: ApexRank
  apexTargetRank: ApexRank
  apexArenaRank: ApexRank
}

type Props = {
  onCreate: (
    data: FormField & { selectedLegends: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  fontColors: string[]
  fonts: { imagePath: string; value: string }[]
  creatorParam: string | null
}

const creatorMapColors: Record<CREATOR, string[]> = {
  riocurara: ['#FCC1B5', '#A5C0F2', '#FACB8F', '#B3D8A8', '#090A0F'],
  nopota__: ['#F6F6F6', '#E1E1E1', '#F5EBE2', '#EBEFFA', '#E1A2B2', '#6E6F71'],
  koharubiyori_51: ['#F5F9C6', '#F5E5F6', '#C0C5FA', '#8EC7FC'],
  o2o2_o2222: ['#98B3EC', '#82CF8E', '#FDC5D1', '#848484'],
  shiina: ['#E0DD3E', '#0385B5', '#AB20AC', '#DF1824'],
  goma_pons: [
    '#FFA5BF',
    '#FBE776',
    '#A7F35D',
    '#5EB7ED',
    '#EDAF5D',
    '#77FF87',
    '#E19AFD',
    '#727272',
  ],
  techi: [
    '#EEFAE6',
    '#CAEAF5',
    '#F0D2F6',
    '#A2D3FE',
    '#FEFEB5',
    '#FDC3E0',
    '#C2D8FF',
    '#C8FED1',
  ],
  _may_0615: [
    '#B22F59',
    '#51A4A4',
    '#FE7F5C',
    '#E9C650',
    '#F0A5CD',
    '#32DF78',
    '#707399',
    '#686266',
  ],
  coffee_mameee: [
    '#F2B34E',
    '#FDDFB2',
    '#F1C8FF',
    '#B3F5FF',
    '#B9E9FC',
    '#C6FEB3',
    '#FDB2DD',
    '#D5D5D5',
  ],
  akarin3331: ['#F08BA9', '#94D9E9', '#F4EEC8', '#E4C6F4'],
  NICOLE_limacina: ['#DDC7E4', '#DDC7E4'],
  _SHIROMISAKANA_: ['#F2A5B9', '#98CBF9', '#78BCC1', '#9F9781'],
  mocchi00561: ['#FFD777'],
  sqtmr: ['#D7EDF1', '#F8E5DF', '#F3EEFB', '#FEFBE3'],
  c_asaaai: ['#D9EAC8'],
  aaa__x_x__: ['#FDEA5B', '#6AD3FF', '#FE9BD1', '#A59BFE'],
  yokoi_0w0: ['#E1C1ED', '#BAD1ED', '#BBD890', '#F0E4CC'],
  ibless0316: ['#8DABF1', '#3A3435', '#FE7CCC', '#D08549'],
  noonereno: ['#364798', '#7DBBB4', '#F2579C', '#F5E988'],
  pyon9956: ['#899BA7', '#FEC3BC', '#FFF8B9', '#B9FEFE', '#E2E2E2', '#D2E2FB'],
}

const creatorMapLegends: Record<CREATOR, string[]> = {
  riocurara: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex1/${index}.png`
    }),
  nopota__: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex2/${index}.png`
    }),
  koharubiyori_51: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex3/${index}.png`
    }),
  o2o2_o2222: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex4/${index}.png`
    }),
  shiina: [
    '/static/images/characters/apex5/0.png',
    '/static/images/characters/apex5/1.png',
    '/static/images/characters/apex5/2.png',
    '/static/images/characters/apex5/3.png',
    '/static/images/characters/apex5/4.png',
    '/static/images/characters/apex5/5.png',
    '/static/images/characters/apex5/6.png',
    '/static/images/characters/apex5/7.png',
    '/static/images/characters/apex5/8.png',
    '/static/images/characters/apex5/9.png',
    '/static/images/characters/apex5/10.png',
    '/static/images/characters/apex5/11.png',
    '/static/images/characters/apex5/12.png',
    '/static/images/characters/apex5/13.png',
    '/static/images/characters/apex5/14.png',
    '/static/images/characters/apex5/15.png',
    '/static/images/characters/apex5/16.png',
    '/static/images/characters/apex5/17.png',
    '/static/images/characters/apex5/18.png',
    '/static/images/characters/apex5/19.png',
  ],
  goma_pons: Array(23)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex7/${index}.png`
    }),
  techi: [
    '/static/images/characters/apex8/0.png',
    '/static/images/characters/apex8/1.png',
    '/static/images/characters/apex8/2.png',
    '/static/images/characters/apex8/3.png',
    '/static/images/characters/apex8/4.png',
    '/static/images/characters/apex8/5.png',
    '/static/images/characters/apex8/6.png',
    '/static/images/characters/apex8/7.png',
    '/static/images/characters/apex8/8.png',
    '/static/images/characters/apex8/9.png',
    '/static/images/characters/apex8/10.png',
    '/static/images/characters/apex8/11.png',
    '/static/images/characters/apex8/12.png',
    '/static/images/characters/apex8/13.png',
    '/static/images/characters/apex8/14.png',
    '/static/images/characters/apex8/15.png',
    '/static/images/characters/apex8/16.png',
    '/static/images/characters/apex8/17.png',
    '/static/images/characters/apex8/18.png',
    '/static/images/characters/apex8/19.png',
    '/static/images/characters/apex8/20.png',
  ],
  _may_0615: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex9/${index}.png`
    }),
  coffee_mameee: Array(22)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex10/${index}.png`
    }),
  akarin3331: Array(22)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex11/${index}.png`
    }),
  NICOLE_limacina: Array(23)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex12/${index}.png`
    }),
  _SHIROMISAKANA_: Array(23)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex13/${index}.png`
    }),
  mocchi00561: Array(23)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex14/${index}.png`
    }),
  sqtmr: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex15/${index}.png`
    }),
  c_asaaai: Array(21)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex16/${index}.png`
    }),
  aaa__x_x__: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex17/${index}.png`
    }),
  yokoi_0w0: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex18/${index}.png`
    }),
  ibless0316: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex19/${index}.png`
    }),
  noonereno: Array(26)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex20/${index}.PNG`
    }),
  pyon9956: Array(25)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/apex21/${index}.png`
    }),
}

const templates = Object.keys(mapApexCreatorInfo).map((creatorName) => {
  return {
    imagePath: mapApexCreatorInfo[creatorName]['profileCardImageURL'],
    value: creatorName,
  }
})

const APEX_GAME_MODES = [
  {
    displayName: 'カジュアル',
    value: 'casual',
  },
  {
    displayName: 'ランク',
    value: 'rank',
  },
  {
    displayName: 'チームデスマッチ',
    value: 'teamDeathMatch',
  },
  {
    displayName: 'カスタム',
    value: 'custom',
  },
] as const

export const EditorApex: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
  fontColors,
  fonts,
  creatorParam
}) => {
  const { t } = useLocale()
  const { handleSubmit, register, watch } = useForm<FormField>({
    defaultValues: {
      playTimeWeekDayBegin: '18',
      playTimeWeekDayEnd: '19',
      playTimeWeekEndBegin: '18',
      playTimeWeekEndEnd: '19',
    },
  })
  const fieldVal = watch()
  const [selectedCreator, setSelectedCreator] = useState<CREATOR>(
    CREATORS.includes(creatorParam as CREATOR)
      ? (creatorParam as CREATOR)
      : CREATORS[0]
  )

  useEffect(() => {
    if (!selectedCreator) return
    const url = new URL(window.location.href)
    url.searchParams.set('creator', selectedCreator)
    window.history.pushState(null, '', url.toString())
  }, [selectedCreator])

  const onChangeCreator = useCallback((creator: CREATOR) => {
    setSelectedCreator(creator)
  }, [])

  const [selectedFontFamily, setSelectedFontFamily] = useState('ap')
  const [selectedColor, setColor] = useState(0)
  const onChangeColor = useCallback((selectedIndex: number) => {
    setColor(selectedIndex)
  }, [])
  const [selectedFontColor, setFontColor] = useState(0)
  const onChangeFontColor = useCallback((selectedIndex: number) => {
    setFontColor(selectedIndex)
  }, [])
  const [selectedSex, setSex] = useState<Sex>(0)
  const onChangeSex = useCallback((selectedIndex: Sex) => {
    setSex(selectedIndex)
  }, [])

  const [selectedPlatform, setPlatform] = useState<Array<Platform>>([0])
  const onChangePlatform = useCallback((selectedIndex: Array<Platform>) => {
    setPlatform(selectedIndex)
  }, [])

  const [selectedVoiceChat, setVoiceChat] = useState<Array<VoiceChat>>([])
  const onChangeVoiceChat = useCallback((selectedIndex: Array<VoiceChat>) => {
    setVoiceChat(selectedIndex)
  }, [])

  const [selectedLegends, setLegends] = useState<number[]>([])
  const onChangeLegends = useCallback((selectedIndex: number[]) => {
    setLegends(selectedIndex)
  }, [])

  const [selectedGameModes, setGameModes] = useState<number[]>([])
  const onChangeGameModes = useCallback((selectedIndex: number[]) => {
    setGameModes(selectedIndex)
  }, [])

  const [clan, setClan] = useState<string>('')

  const handleCreate = (data: FormField) => {
    onCreate(
      { ...data, selectedLegends },
      creatorMapTargetId[selectedCreator],
      selectedSex,
      selectedPlatform,
      selectedVoiceChat
    )
  }

  const lastModText = format(new Date(), 'yyyy年MM月dd日')
  return (
    <>
      <Image
        width="1920"
        height="1080"
        priority={true}
        unoptimized={true}
        src={`/static/images/promotes/apex.jpg`}
        alt={`Apex自己紹介カード作成ツール`}
      />
      <DescriptionSection>
        <Time>
          <time dateTime={new Date().toISOString()} itemProp="datepublished" />
          <time dateTime={new Date().toISOString()} itemProp="modified">
            {t.最終更新}: {lastModText}
          </time>
        </Time>
        <H1Text>{t.ApexLegendsかわいい自己紹介カード作成ツール}</H1Text>
        <H2Text>{t.ApexLegendsかわいい自己紹介カード作成ツール説明文}</H2Text>
        <ProfileCardEditorDescription />
      </DescriptionSection>
      <PreviewContainer>
        <PreviewCardContainer>
          {selectedCreator === 'riocurara' && (
            <Apex1
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'nopota__' && (
            <Apex2
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'koharubiyori_51' && (
            <Apex3
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              apexArenaRank={fieldVal.apexArenaRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
              clan={clan}
            />
          )}
          {selectedCreator === 'o2o2_o2222' && (
            <Apex4
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'shiina' && (
            <Apex5
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'goma_pons' && (
            <Apex7
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'techi' && (
            <Apex8
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === '_may_0615' && (
            <Apex9
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'coffee_mameee' && (
            <Apex10
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'akarin3331' && (
            <Apex11
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexStartingSeason={fieldVal.apexStartingSeason}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              apexArenaRank={fieldVal.apexArenaRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'NICOLE_limacina' && (
            <Apex12
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === '_SHIROMISAKANA_' && (
            <Apex13
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              inviteStyle={fieldVal.inviteStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'mocchi00561' && (
            <Apex14
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              memo={fieldVal.memo}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'sqtmr' && (
            <Apex15
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'c_asaaai' && (
            <Apex16
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'aaa__x_x__' && (
            <Apex17
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'yokoi_0w0' && (
            <Apex18
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
              gameModes={selectedGameModes}
            />
          )}
          {selectedCreator === 'ibless0316' && (
            <Apex19
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              twitterId={fieldVal.twitterId}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'noonereno' && (
            <Apex20
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              apexTargetRank={fieldVal.apexTargetRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
          {selectedCreator === 'pyon9956' && (
            <Apex21
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              apexFavoriteWeapon={fieldVal.apexFavoriteWeapon}
              apexMaxDamage={fieldVal.apexMaxDamage}
              apexKD={fieldVal.apexKD}
              apexRank={fieldVal.apexRank}
              apexTargetRank={fieldVal.apexTargetRank}
              hitokoto={fieldVal.hitokoto}
              legends={selectedLegends}
            />
          )}
        </PreviewCardContainer>
        <ProfileEditorAppBanner />
      </PreviewContainer>

      <div>
        <FormContainer>
          <form
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            onSubmit={handleSubmit(handleCreate)}
          >
            <FormItemContainer>
              <VerticalContainer>
                <SectionText>{t.テンプレートを選ぶ}</SectionText>
                <TemplateSelector
                  values={templates}
                  onChange={onChangeCreator}
                  value={selectedCreator}
                />
              </VerticalContainer>
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.カードの背景色を選ぶ}</SectionText>
              <ColorSelector
                // templateで可変
                colors={creatorMapColors[selectedCreator]}
                value={selectedColor}
                onChange={onChangeColor}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.文字色を選ぶ}</SectionText>
              <ColorSelector
                colors={fontColors}
                value={selectedFontColor}
                onChange={onChangeFontColor}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.フォントを選ぶ}</SectionText>
              <ImageSelector
                values={fonts}
                onChange={setSelectedFontFamily as any}
                value={selectedFontFamily}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.なまえ}</SectionText>
              <InputText {...register('name', { maxLength: 20 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.ID}</SectionText>
              <InputText {...register('id', { maxLength: 20 })} />
            </FormItemContainer>
            {selectedCreator === 'akarin3331' && (
              <FormItemContainer>
                <SectionText>{t.はじめた時期}</SectionText>
                <InputText
                  {...register('apexStartingSeason', { maxLength: 30 })}
                />
              </FormItemContainer>
            )}
            <FormItemContainer>
              <SectionText>{t.性別}</SectionText>
              <SexSelector val={selectedSex} onChange={onChangeSex} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プラットフォーム}</SectionText>
              <PlatformSelector
                onChange={onChangePlatform}
                value={selectedPlatform}
                activeValues={
                  selectedCreator === 'riocurara'
                    ? [0, 1, 2, 3]
                    : selectedCreator === 'koharubiyori_51'
                    ? [0, 1, 2, 3, 4]
                    : selectedCreator === 'nopota__'
                    ? [1, 0, 2, 3]
                    : selectedCreator === 'goma_pons'
                    ? [1, 0, 2, 3]
                    : selectedCreator === 'shiina'
                    ? [0, 1, 3]
                    : selectedCreator === 'techi'
                    ? [0, 1, 2, 3, 4]
                    : selectedCreator === '_may_0615'
                    ? [0, 2, 1, 3, 4]
                    : selectedCreator === 'coffee_mameee'
                    ? [0, 1, 2, 3, 4]
                    : selectedCreator === 'akarin3331'
                    ? [0, 1, 2, 3, 4]
                    : selectedCreator === '_SHIROMISAKANA_'
                    ? [1, 3, 0, 2]
                    : selectedCreator === 'sqtmr'
                    ? [0, 1, 2, 3]
                    : selectedCreator === 'yokoi_0w0'
                    ? [0, 1, 2, 3]
                    : [0, 1, 2, 3]
                }
              />
            </FormItemContainer>
            {selectedCreator !== 'koharubiyori_51' &&
              selectedCreator !== 'goma_pons' &&
              selectedCreator !== 'techi' &&
              selectedCreator !== '_may_0615' &&
              selectedCreator !== '_SHIROMISAKANA_' &&
              selectedCreator !== 'mocchi00561' &&
              selectedCreator !== 'pyon9956' && (
                <FormItemContainer>
                  <SectionText>{t.プレイスタイル}</SectionText>
                  <InputText
                    {...register('playStyle', { maxLength: 20 })}
                  />
                </FormItemContainer>
              )}
            {selectedCreator === '_SHIROMISAKANA_' && (
              <FormItemContainer>
                <SectionText>{t.招待スタイル}</SectionText>
                <InputSelector {...register('inviteStyle')}>
                  <option value="dm">DM</option>
                  <option value="comment">募集コメント</option>
                  <option value="lobby">ロビー待機OK</option>
                </InputSelector>
              </FormItemContainer>
            )}
            {selectedCreator === '_SHIROMISAKANA_' && (
              <FormItemContainer>
                <SectionText>{t.プレイスタイル}</SectionText>
                <InputSelector {...register('playStyle')}>
                  <option value="vanguard">前衛</option>
                  <option value="middleGuard">中衛</option>
                  <option value="rearGuard">後衛</option>
                </InputSelector>
              </FormItemContainer>
            )}
            <FormItemContainer>
              <SectionText>{t.ボイスチャット}</SectionText>
              <VoiceChatSelector
                onChange={onChangeVoiceChat}
                value={selectedVoiceChat}
                activeValue={
                  selectedCreator === 'riocurara'
                    ? [0, 1, 2, 3, 4]
                    : selectedCreator === 'koharubiyori_51'
                    ? [2, 3, 4]
                    : selectedCreator === 'nopota__'
                    ? [2, 3, 0, 1, 4]
                    : selectedCreator === 'shiina'
                    ? [3, 2, 5, 1]
                    : selectedCreator === 'techi'
                    ? [0, 2, 1, 3, 4]
                    : selectedCreator === '_may_0615'
                    ? [0, 1, 2, 3, 5, 4]
                    : selectedCreator === 'coffee_mameee'
                    ? [0, 1, 2, 3, 4]
                    : selectedCreator === 'sqtmr'
                    ? [1, 2, 3, 4]
                    : selectedCreator === 'aaa__x_x__'
                    ? [0, 2, 1, 3, 4]
                    : selectedCreator === 'yokoi_0w0'
                    ? [0, 2, 1, 3, 99, 4]
                    : selectedCreator === 'pyon9956'
                    ? [3, 0, 2, 1, 4]
                    : [0, 1, 2, 3, 4]
                }
              />
            </FormItemContainer>
            {selectedCreator === 'ibless0316' && (
              <FormItemContainer>
                <SectionText>Twitter ID</SectionText>
                <InputText {...register('twitterId', { maxLength: 20 })} />
              </FormItemContainer>
            )}
            <FormItemContainer>
              <SectionText>{t.プレイ時間帯平日}</SectionText>
              <InputSelector {...register('playTimeWeekDayBegin')}>
                <OptionsPlaytime />
              </InputSelector>
              <TimeSelectorText>{t.から}</TimeSelectorText>
              <InputSelector {...register('playTimeWeekDayEnd')}>
                <OptionsPlaytime />
              </InputSelector>
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プレイ時間帯土日}</SectionText>
              <InputSelector {...register('playTimeWeekEndBegin')}>
                <OptionsPlaytime />
              </InputSelector>
              <TimeSelectorText>{t.から}</TimeSelectorText>
              <InputSelector {...register('playTimeWeekEndEnd')}>
                <OptionsPlaytime />
              </InputSelector>
            </FormItemContainer>
            {selectedCreator !== 'o2o2_o2222' &&
              selectedCreator !== 'shiina' &&
              selectedCreator !== 'NICOLE_limacina' &&
              selectedCreator !== 'noonereno' && (
                <FormItemContainer>
                  <SectionText>{t.プレイ時間帯自由記述}</SectionText>
                  <InputText {...register('playTime', { maxLength: 100 })} />
                </FormItemContainer>
              )}

            {selectedCreator === 'yokoi_0w0' && (
              <FormItemContainer>
                <SectionText>{t.ゲームモード}</SectionText>
                <TextSelector
                  values={APEX_GAME_MODES}
                  onChange={onChangeGameModes}
                />
              </FormItemContainer>
            )}
            {selectedCreator !== 'NICOLE_limacina' &&
              selectedCreator !== 'noonereno' && (
                <FormItemContainer>
                  <SectionText>{t.好きな武器}</SectionText>
                  <InputText
                    {...register('apexFavoriteWeapon', { maxLength: 100 })}
                  />
                </FormItemContainer>
              )}
            <FormItemContainer>
              <SectionText>{t.よく使うレジェンド}</SectionText>
              <ImagesSelector
                values={creatorMapLegends[selectedCreator]}
                onChange={onChangeLegends}
              />
            </FormItemContainer>
            {selectedCreator !== 'o2o2_o2222' &&
              selectedCreator !== 'shiina' &&
              selectedCreator !== '_SHIROMISAKANA_' && (
                <FormItemContainer>
                  <SectionText>{t.KD}</SectionText>
                  <InputText
                    type="number"
                    step="0.01"
                    {...register('apexKD')}
                  />
                </FormItemContainer>
              )}
            {selectedCreator !== 'o2o2_o2222' &&
              selectedCreator !== 'shiina' && (
                <FormItemContainer>
                  <SectionText>{t.最高ダメージ}</SectionText>
                  <InputText
                    type="number"
                    {...register('apexMaxDamage')}
                  />
                </FormItemContainer>
              )}
            <FormItemContainer>
              <SectionText>
                {selectedCreator === 'noonereno' ? t.最高ランク : t.ランク}
              </SectionText>
              <InputSelector {...register('apexRank')}>
                <option value="rookie">{t.ルーキー}</option>
                <option value="bronze">{t.ブロンズ}</option>
                <option value="silver">{t.シルバー}</option>
                <option value="gold">{t.ゴールド}</option>
                <option value="platinum">{t.プラチナ}</option>
                <option value="diamond">{t.ダイヤ}</option>
                <option value="master">{t.マスター}</option>
                <option value="predator">{t.プレデター}</option>
              </InputSelector>
            </FormItemContainer>
            {selectedCreator === 'noonereno' && (
              <FormItemContainer>
                <SectionText>{t.目標ランク}</SectionText>
                <InputSelector {...register('apexTargetRank')}>
                  <option value="rookie">{t.ルーキー}</option>
                  <option value="bronze">{t.ブロンズ}</option>
                  <option value="silver">{t.シルバー}</option>
                  <option value="gold">{t.ゴールド}</option>
                  <option value="platinum">{t.プラチナ}</option>
                  <option value="diamond">{t.ダイヤ}</option>
                  <option value="master">{t.マスター}</option>
                  <option value="predator">{t.プレデター}</option>
                </InputSelector>
              </FormItemContainer>
            )}
            {selectedCreator === 'akarin3331' && (
              <FormItemContainer>
                <SectionText>{t.アリーナランク}</SectionText>
                <InputSelector {...register('apexArenaRank')}>
                  <option value="arenaRookie">{t.ルーキー}</option>
                  <option value="arenaBronze">{t.ブロンズ}</option>
                  <option value="arenaSilver">{t.シルバー}</option>
                  <option value="arenaGold">{t.ゴールド}</option>
                  <option value="arenaPlatinum">{t.プラチナ}</option>
                  <option value="arenaDiamond">{t.ダイヤ}</option>
                  <option value="arenaMaster">{t.マスター}</option>
                  <option value="arenaPredator">{t.プレデター}</option>
                </InputSelector>
              </FormItemContainer>
            )}
            {selectedCreator === 'mocchi00561' && (
              <FormItemContainer>
                <SectionText>{t.好き放題言っちゃおう}</SectionText>
                <InputText {...register('memo', { maxLength: 100 })} />
              </FormItemContainer>
            )}
            <FormItemContainer>
              <SectionText>{t.ひとこと}</SectionText>
              <InputText {...register('hitokoto', { maxLength: 50 })} />
            </FormItemContainer>
            <ButtonDescriptionText>
              {t.カードの作成にはSNS連携が必要です次の画面でログインをします}
            </ButtonDescriptionText>
            <Submit
              id="create_profile"
              type="submit"
              value={t.これでカードをつくる}
              disabled={submitButtonDisable}
            />
          </form>
        </FormContainer>
      </div>
    </>
  )
}

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  padding-bottom: 40px; /* Add padding to make space for the fixed banner */
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 20px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`

const DescriptionSection = styled.div`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
  margin-bottom: 30px;
`

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 13px;
`

const H1Text = styled.h1`
  margin-top: 12px;
  font-size: 18px;
  font-weight: bold;
  line-height: 20px;
  color: ${colors.text.primary};
  text-align: center;
  line-height: 1.6;
  display: flex;
`

const H2Text = styled.h2`
  margin-top: 20px;
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.6;
  display: flex;
`

const SectionText = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  display: flex;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const ButtonDescriptionText = styled.h3`
  margin: auto;
  margin-top: 30px;
  width: 100%;
  font-size: 10px;
  color: ${colors.text.secondary};
  justify-content: center;
  display: flex;
`

const Submit = styled.input`
  margin-top: 12px;
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`

const VerticalContainer = styled.div`
  display: flex;
  flex-flow: column;
`
